<template>
  <div :class="[
    'UIElement',
    $style.Calendar
  ]">
    <div :class="$style.Calendar_header">
      <Button
        :class="$style.Calendar_button"
        type="plain"
        icon="fa fa-chevron-left"
        @click="move('month', -1)"
      />
      <span
        :class="$style.Calendar_monthName"
      >{{ data.monthName }}</span>
      <Button
        :class="$style.Calendar_button"
        type="plain"
        icon="fa fa-chevron-right"
        @click="move('month', 1)"
      />
    </div>
    <div :class="$style.Calendar_weekDays">
      <span
        v-for="name in data.weekdays"
        :key="name"
        :class="$style.Calendar_weekday"
      >{{ name }}</span>
    </div>
    <div :class="$style.Calendar_days">
      <span
        v-for="day in data.dates"
        :key="day.time"
        :class="[
          $style.Calendar_day,
          day.isSelected && $style.Calendar_day__selected,
          day.isToday && $style.Calendar_day__today,
          !day.isSameMonth && $style.Calendar_day__otherMonth
        ]"
        :disabled="day.isDisabled"
        @click="handleSelect(day)"
      >{{day.date}}</span>
    </div>
    <div
      v-if="withTime"
      :class="$style.CalendarTime"
    >
      <select
        :class="$style.TimeSelect"
        :value="currentHour"
        @change="setTimeUnit($event, 'hour')"
      >
        <option
          v-for="n in 24"
          :key="n"
          :value="n - 1"
        >{{pad(n - 1)}}</option>
      </select>
      <select
        :class="$style.TimeSelect"
        :value="currentMinute"
        @change="setTimeUnit($event, 'minute')"
      >
        <option
          v-for="n in 60"
          :key="n"
          :value="n - 1"
        >{{pad(n - 1)}}</option>
      </select>
    </div>
    <!-- <pre>{{data}}</pre> -->
  </div>
</template>

<script>
import moment from 'moment'
import * as utils from './utils'
import {isValidMoment} from '../../utils'
import {Button} from '../Button'

export default {
  components: {
    Button
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    /**
     * The underlying model value represented as `YYYY-MM-DD`.
     */
    value: {
      type: String
    },
    /**
     * Format of the underling value.
     *
     * @see https://momentjs.com/docs/#/displaying/format/
     */
    modelFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    /**
     * Whether to allow editing time.
     */
    withTime: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      date: null
    }
  },
  created() {
    return this.$watch('value', () => {
      const date = isValidMoment(this.value)
        ? moment(this.value, this.modelFormat, true)
          .startOf('month')
        : moment().startOf('month')
      this.date = date
    }, {immediate: true})
  },
  computed: {
    data() {
      return utils.getMonthData({
        selected: this.value,
        year: this.date.year(),
        month: this.date.month()
        // minDate: moment()
      })
    },
    currentHour() {
      return moment(this.value).hour()
    },
    currentMinute() {
      return moment(this.value).minute()
    }
  },
  methods: {
    move(unit, amount) {
      this.date = moment(this.date).add(amount, unit)
    },
    handleSelect(day) {
      const value = moment(day.time)
        .hour(this.currentHour)
        .minute(this.currentMinute)
        .format(this.modelFormat)
      this.$emit('change', value)
    },
    pad(value) {
      return value < 10 ? '0' + value : value
    },
    setTimeUnit(event, unit) {
      if (unit !== 'hour' && unit !== 'minute') {
        throw new Error(`Unexpected unit '${unit}'`)
      }
      const date = moment(this.value, this.modelFormat)
      if (date.isValid()) {
        date[unit](parseInt(event.target.value))
        this.$emit('change', date.format(this.modelFormat))
      }
    }
    // resetTime() {
    //   const date = moment(this.value, this.modelFormat)
    //   if (date.isValid()) {
    //     date.hour(0).minute(0).second(0)
    //     this.$emit('change', date.format(this.modelFormat))
    //   }
    // }
  }
}
</script>

<style lang="scss" module>
@import "../../styles/variables";

$size: 30px;
$selectedColor: #42526e;

.Calendar {
  width: 280px;
}

.Calendar_header {
  display: flex;
  align-items: center;
  padding: 0 5px;
}

.Calendar_button {
  color: #627381;
}

.Calendar_monthName {
  flex: 1;
  color: #172a4c;
  font-weight: 600;
  text-align: center;
}

.Calendar_day, .Calendar_weekday {
  display: inline-block;
  width: calc(100% / 7);
  margin-left: -1px;
  margin-top: -1px;
  padding: 9px;
  text-align: center;
}

.Calendar_weekday {
  color: #6b778c;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.Calendar_day {
  position: relative;
  // border: 1px solid #c4cdd5;
  border-radius: 3px;
  color: #091d41;
  font-weight: 500;

  &:hover {
    background: #ebecf0;
    cursor: pointer;
  }

  &[disabled] {
    background: #f9fafb;
    color: #dfe1e6;
    cursor: not-allowed;
  }
}

.Calendar_day__otherMonth {
  color: #6b778c;
}

.Calendar_day__selected {
  background: $selectedColor;
  color: #fff;

  &:hover {
    background: darken($selectedColor, 5%);
  }
}

.Calendar_day__today {
  &::after {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid $focusColor;
    border-radius: 3px;
  }
}

.CalendarTime {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  font-size: 16px;
}

.TimeSelect {
  min-width: 50px;
  margin-right: 8px;
  font-size: 16px;
  border: none;
  background: #ebecf0;
}
.ResetTime {
  color: #666;

  &:hover {
    cursor: pointer;
    color: #000;
  }
}
</style>
